import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

export type Tab = {
  /** A stable key for the tab, unique within the set. */
  id: string
  /** The tab's visible name. */
  label: string
  /** What the tab's panel shows. */
  content: ReactNode
}

export type TabsProps = {
  tabs: Tab[]
  /** Names the tab list for screen readers, such as "Application details". */
  label: string
  /** The tab selected first; the first tab when left out. */
  defaultTab?: string
  /** Called with the id of the newly selected tab. */
  onChange?: (id: string) => void
}

/**
 * Switches between panels of related content in one place. Follows the
 * WAI-ARIA tabs pattern with automatic activation: arrow keys move between
 * tabs and select them, Home and End jump to the first and last, and only the
 * selected tab is in the tab order.
 */
export default function Tabs({ tabs, label, defaultTab, onChange }: TabsProps) {
  const base = useId()
  const [selected, setSelected] = useState(() => (tabs.some((tab) => tab.id === defaultTab) ? defaultTab! : tabs[0]?.id))
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const current = tabs.find((tab) => tab.id === selected) ?? tabs[0]

  const select = (index: number) => {
    const tab = tabs[index]
    if (!tab) return
    setSelected(tab.id)
    onChange?.(tab.id)
    refs.current[index]?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.id === current?.id)
    const last = tabs.length - 1
    if (event.key === 'ArrowRight') select(index === last ? 0 : index + 1)
    else if (event.key === 'ArrowLeft') select(index === 0 ? last : index - 1)
    else if (event.key === 'Home') select(0)
    else if (event.key === 'End') select(last)
    else return
    event.preventDefault()
  }

  if (!current) return null

  return (
    <div className="mds-tabs">
      <div role="tablist" aria-label={label} className="mds-tabs__list" onKeyDown={onKeyDown}>
        {tabs.map((tab, index) => {
          const isSelected = tab.id === current.id
          return (
            <button
              key={tab.id}
              ref={(node) => {
                refs.current[index] = node
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${index}`}
              aria-selected={isSelected}
              aria-controls={`${base}-panel-${index}`}
              tabIndex={isSelected ? 0 : -1}
              className="mds-tabs__tab"
              onClick={() => select(index)}
            >
              {tab.label}
            </button>
          )
        })}
      </div>
      {tabs.map((tab, index) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${base}-panel-${index}`}
          aria-labelledby={`${base}-tab-${index}`}
          tabIndex={0}
          hidden={tab.id !== current.id}
          className="mds-tabs__panel"
        >
          {tab.content}
        </div>
      ))}
    </div>
  )
}
